(ns app.front.view.group.admin.workdays-editor.core
  (:require [app.calendar :as calendar]
            [app.domain.blackout.model :as blackout-model]
            [app.domain.booking.model :as booking-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.inspections.time-slots-editor :as inspection-time-slots-editor]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(rf/reg-event-fx ::init
  (fn [_ [_ group-id]]
    {:dispatch [::fetch-workdays-data group-id]}))

(rf/reg-event-fx ::fetch-workdays-data
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/workdays-data
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [:app.front.entities/merge-entity-db]}}))

(rf/reg-sub ::workdays
  :<- [::gas/group-availability]
  :-> booking-model/workdays-from-availability)

(rf/reg-event-fx ::save-workdays
  (fn [_ [_ group-id working-days]]
    {:comm/command {:command    :inspection/set-working-days
                    :data       (util/mmap group-id working-days)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :inspection/working-days-saved}}}))

(rf/reg-sub ::blackouts
  :<- [::gas/group]
  :<- [::gas/superdomain]
  :<- [::gas/group-in-superdomain?]
  :<- [::gas/use-superdomain-calendar?]
  (fn [[group superdomain in-superdomain? superdomain-calendar?] _]
    (let [blackout-group (if (and in-superdomain? superdomain-calendar?)
                           superdomain
                           group)]
      (->> blackout-group
           blackout-model/current-group-blackouts
           (sort-by :blackout/start-time)
           (reduce (fn [acc {:blackout/keys [^js start-time] :as blackout}]
                     (update-in acc [(.getFullYear start-time) (.getMonth start-time)] conj blackout))
                   {})))))

(defn set-time-to-day [tz {:blackout/keys [start-time end-time]}]
  {:blackout/start-time (date-util/date-at-time-in-tz start-time 0 0 tz)
   :blackout/end-time   (date-util/date-at-time-in-tz end-time 23 59 tz)})

(rf/reg-event-fx ::add-blackout
  (fn [{:keys [db]} [_ group blackout]]
    {:db           (assoc-in db [::data ::loading?] true)
     :comm/command {:command          :group/add-blackout
                    :data             {:blackout (merge blackout
                                                        (set-time-to-day (:group/tz group) blackout))
                                       :group-id (:db/id group)}
                    :on-success       [::add-blackout.success]
                    :finally-dispatch [::add-blackout.done]
                    :notify           {:success :blackout/add-success}}}))

(rf/reg-event-fx ::add-blackout.success
  (fn [{:keys [db]} [_ status changes]]
    {:db       (p/dissoc-in db [::data ::new-blackout])
     :dispatch [::entities/apply-changes status changes]}))

(rf/reg-event-db ::add-blackout.done
  (fn [db _]
    (p/dissoc-in db [::data ::loading?])))

(rf/reg-event-fx ::delete-blackout
  (fn [_ [_ blackout-id]]
    {:comm/command {:command    :group/delete-blackout
                    :data       (util/mmap blackout-id)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :blackout/remove-success}}}))

(rf/reg-event-fx ::fetch-public-holidays
  (fn [{:keys [db]} [_ group-id]]
    {:db           (assoc-in db [::data ::loading?] true)
     :comm/command {:command          :group/fetch-public-holidays
                    :data             {:group-id group-id}
                    :on-success       [::fetch-public-holidays.success]
                    :finally-dispatch [::add-blackout.done]
                    :notify           {:success :blackout/fetch-public-holidays-success}}}))

(rf/reg-event-fx ::fetch-public-holidays.success
  (fn [{:keys [db]} [_ status changes]]
    {:db       (p/dissoc-in db [::data ::new-blackout])
     :dispatch [::entities/apply-changes status changes]}))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::loading?
  :<- [::data]
  :-> ::loading?)

(defn sanitize [{:blackout/keys [start-time end-time] :as v}]
  (if (< end-time start-time)
    (assoc v :blackout/end-time start-time)
    v))

(rf/reg-event-db ::set-new-blackout-value
  (fn [db [_ k v]]
    (-> db
        (assoc-in [::data ::new-blackout k] v)
        (update-in [::data ::new-blackout] sanitize))))

(rf/reg-event-db ::cancel-add-new-blackout
  (fn [db _]
    (p/dissoc-in db [::data ::new-blackout])))

(rf/reg-sub ::new-blackout-data
  :<- [::data]
  :-> ::new-blackout)

(rf/reg-sub ::blackout-valid?
  :<- [::new-blackout-data]
  (fn [{:blackout/keys [start-time end-time descriptions]}]
    (and start-time
         end-time
         (>= end-time start-time)
         (->> (vals descriptions)
              (some #(not (str/blank? %)))))))

(rf/reg-sub ::min-date
  :<- [::new-blackout-data]
  (fn [data [_ k]]
    (case k
      :blackout/start-time (date-util/today-start-of-day)
      :blackout/end-time (some-> data :blackout/start-time date-util/start-of-day)
      nil)))

(rf/reg-event-fx ::set-superdomain-calendar-usage
  (fn [_ [_ tr group superdomain use-superdomain-calendar?]]
    {:comm/command {:command    :group/set-superdomain-calendar-usage
                    :data       {:authority-group-id    (:db/id group)
                                 :superdomain-calendar? use-superdomain-calendar?}
                    :on-success [::entities/apply-changes]
                    :notify     {:success (tr [:superdomain/calendar-toggle] [(if use-superdomain-calendar?
                                                                                (:group/name superdomain)
                                                                                (:group/name group))])}}}))

(defn existing-blackout [{:keys [tz tr lang use-superdomain-calendar?]}
                         {:blackout/keys [start-time end-time subtype] :as blackout}]
  (let [format-date   (<sub [::date-util/yearless-date-fn])
        display-start (date-util/to-tz start-time tz)
        display-end   (some-> end-time (date-util/to-tz tz))]
    [:div.w-full.rounded.p-3.pr-2.mb-2.flex.items-center.justify-between.border-4.border-solid
     {:data-cy "single-blackout"
      :class   (if (= subtype :blackout.subtype/general)
                 "bg-ui-violet-30 border-ui-violet-30"
                 "border-4 border-solid border-ui-blue-30")}
     [:div.flex
      [:div.font-bold.mr-2
       (str (format-date display-start)
            (when (and display-end
                       (not (date-util/same-day? display-start display-end)))
              (str " - " (format-date display-end))))]
      [:div (blackout-model/description blackout lang)]]
     [ui/modal-confirmation-button
      {:tr tr
       :id (str "remove-blackout-" (:db/id blackout))

       :trigger-button-opts {:kind             :ghost
                             :size             :small
                             :icon             i/remove
                             :icon-description (tr [:delete])
                             :class-name       "only-icon-button"
                             :disabled         use-superdomain-calendar?}
       :modal-title         (tr [:blackout/remove-modal-title])
       :on-yes-click        #(>evt [::delete-blackout (:db/id blackout)])}
      (tr [:blackout/remove-modal-text])]]))

(defn month-view [{:keys [tr] :as ctx} [month blackouts]]
  [:div.w-full.md:w-1-2.xl:w-1-3.xxl:w-1-4.flex.flex-col.px-2
   [:div.text-lg.my-4
    (tr [(keyword "date.month" (str month))])]
   (for-frag [blackout (sort-by :blackout/start-time blackouts)]
     [existing-blackout ctx blackout])])

(defn year-view [ctx [year blackout-months]]
  [:div.mt-4
   [:div.border-b-2.border-solid.border-ui-gray.text-xl
    year]
   [:div.flex.flex-wrap.-mx-2
    (->> blackout-months
         (mapv (fn [month+blackouts]
                 [month-view ctx month+blackouts]))
         (cons :<>)
         vec)]])

(defn date-picker [{:keys [tr k data]}]
  [ui/date-picker {:tr          tr
                   :light       true
                   :required?   true
                   :width       "100%"
                   :class-input "bx--text-input--light"
                   :label-text  (tr [k])
                   :callback    #(>evt [::set-new-blackout-value k %])
                   :value       (get data k)
                   :min-date    (<sub [::min-date k])}])

(defn add-new-form [{:keys [tr data group use-superdomain-calendar?]}]
  [:div.flex.justify-end>div.w-full.md:w-1-2.xl:w-1-3.xxl:w-1-4
   [ui-add/add-widget
    {:loading? (<sub [::loading?])
     :buttons  [{:text     (tr [:cancel])
                 :on-click #(>evt [::cancel-add-new-blackout])}
                {:text      (tr [:save])
                 :disabled? (or (not (<sub [::blackout-valid?]))
                                use-superdomain-calendar?)
                 :on-click  #(>evt [::add-blackout group data])}]}
    [:div.bold.flex.flex-col.gap-3
     [date-picker (util/mmap {:k :blackout/start-time} tr data)]
     [date-picker (util/mmap {:k :blackout/end-time} tr data)]
     [ui-select/select {:id           "new-blackout-subtype"
                        :class        "full-width-select"
                        :required     true
                        :label-text   (tr [:blackout/subtype])
                        :values       blackout-model/blackout-subtypes
                        :value        (:blackout/subtype data)
                        :allow-empty? false
                        :light        true
                        :val->str     (comp tr vector)
                        :on-change    #(>evt [::set-new-blackout-value :blackout/subtype %])}]
     [:div
      [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
      [ui-i18n/localized-string-edit
       {:on-change           #(rf/dispatch-sync [::set-new-blackout-value :blackout/descriptions %])
        :on-end-edit         #(>evt [::set-new-blackout-value :blackout/descriptions %])
        :strings             (:blackout/descriptions data)
        :label-text          (tr [:blackout/descriptions])
        :required            true
        :allowed-locales-sub ::gas/allowed-locales}]]]]])

(defn holiday-editor [tr use-superdomain-calendar?]
  (let [lang         (<sub [:language])
        blackout-map (<sub [::blackouts])
        group        (<sub [::gas/group])
        data         (<sub [::new-blackout-data])
        tz           (:group/tz group)
        ctx          (util/mmap {:group-id (:db/id group)}
                                tz
                                tr
                                lang
                                use-superdomain-calendar?)]
    [:<>
     [ui/time-zone-info {:group-tz             tz
                         :tz-format            "longGeneric"
                         :tr-key               :date/time-zone-info
                         :hide-when-identical? true
                         :class                "mb-4"}]
     [:div.mb-4
      (->> blackout-map
           (mapv (fn [year+blackout-months]
                   [year-view ctx year+blackout-months]))
           (cons :<>)
           vec)]
     (if (seq data)
       [add-new-form (util/mmap tr data group use-superdomain-calendar?)]
       [ui-action-buttons/action-buttons
        {:buttons [{:icon      i/refresh
                    :disabled? use-superdomain-calendar?
                    :text      (tr [:blackout/fetch-public-holidays])
                    :on-click  #(>evt [::fetch-public-holidays (:db/id group)])}
                   {:icon      i/plus
                    :disabled? use-superdomain-calendar?
                    :on-click  #(>evt [::set-new-blackout-value :blackout/subtype :blackout.subtype/general])
                    :text      (tr [:blackout/add-new])}]}])]))

(defn working-days-editor [tr superdomain-calendar?]
  (r/with-let [*confirm-modal-dispatch (r/atom nil)]
    (let [workdays (<sub [::workdays])
          group-id (<sub [::gas/group-id])]
      [:div
       [ui/confirm-modal
        {:open?        (boolean @*confirm-modal-dispatch)
         :id           "workdays"
         :tr           tr
         :on-close     #(reset! *confirm-modal-dispatch nil)
         :on-yes-click #(>evt @*confirm-modal-dispatch)}
        (tr [:inspection/remove-working-day-confirm])]
       (for-frag [weekday calendar/weekdays
                  :let [day-name  (futil/tr-weekday tr weekday)
                        selected? (boolean (workdays weekday))]]
         (cc/checkbox {:id         (str "day-" weekday)
                       :checked    selected?
                       :disabled   (true? superdomain-calendar?)
                       :label-text day-name
                       :title      day-name
                       :on-change  (fn [_]
                                     (if selected?
                                       (reset! *confirm-modal-dispatch [::save-workdays
                                                                        group-id
                                                                        (disj workdays weekday)])
                                       (>evt [::save-workdays
                                              group-id
                                              (conj workdays weekday)])))}))])))

(defn view [match]
  (let [tr                        (<sub [::gas/group-tr])
        group                     (<sub [::gas/group])
        superdomain               (<sub [::gas/superdomain])
        group-id                  (<sub [::gas/group-id])
        group-in-superdomain?     (<sub [::gas/group-in-superdomain?])
        superdomain-calendar-ff?  (ff/enabled? :feature-flag.id/superdomain-working-day-calendar)
        inspections?              (<sub [::gas/group-or-descendant-has-feature? :group.feature/inspections])
        use-superdomain-calendar? (<sub [::gas/use-superdomain-calendar?])]
    [acommon/admin-page
     {:match match
      :title (tr [:view/group.admin.workdays-editor])}
     (when (and group-in-superdomain? superdomain-calendar-ff?)
       [section/section
        "superdomain-calendar-toggle"
        {:tr tr}
        :superdomain/use-calendar-title
        [:<>
         (cc/toggle {:toggled    use-superdomain-calendar?
                     :label-text (tr [:superdomain/use-calendar-legend])
                     :id         (str "shared-service-calendar-toggle-" group-id)
                     :labelA     (tr [:no])
                     :labelB     (tr [:yes])
                     :on-toggle  #(>evt [::set-superdomain-calendar-usage tr group superdomain %])})]])
     (when inspections?
       [section/section
        "working-days"
        {:tr tr}
        :inspection/working-days
        [:<>
         [:p.pb-0 (tr [:inspection/working-days-legend])]
         [ui/help (tr [:inspection/working-days-time-slots-note])]]
        [working-days-editor tr use-superdomain-calendar?]])
     [section/section
      "non-working-days"
      {:tr tr}
      :blackout/title
      [:p.lg:w-2-3.xxl:w-1-2.pb-4
       (tr [:blackout/instruction])]
      [holiday-editor tr use-superdomain-calendar?]]
     (when (and inspections? superdomain-calendar-ff?)
       [section/section
        "inspection-time-slots"
        {:tr tr}
        :inspection/time-slots
        [:p.lg:w-2-3.xxl:w-1-2.pb-4
         (tr [:inspection/time-slots-legend])]
        [inspection-time-slots-editor/view {:tr tr}]])]))

(def route
  {:name        :view/group.admin.workdays-editor
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{:keys [group-id]} :path}]
                                (>evt [::init group-id]))}]})
